import { useMemo } from 'react';

import { convertToGeneratedApiParams, useGetScheduleEventsQuery } from '../store/api';
import { useAppointmentQueryParams } from '../store/hooks';
import type {
  BlockedTimeOccurrence,
  ScheduleBlockedTimeOccurrenceResponse,
} from '../types/blockedTime';
import { blockedTimeOccurrenceFromScheduleEvent } from '../utils/blockedTime';

import { useOrgScheduleAccess } from './useOrgScheduleAccess';
import { useOrgScheduleFilters } from './useOrgScheduleFilters';

export function useBlockedTimesForCalendar(visibleResourceIds: string[]) {
  const queryParams = useAppointmentQueryParams();
  const { hasOrgScheduleAccess } = useOrgScheduleAccess();
  const { isOrgViewActive, selectedProviderIds } = useOrgScheduleFilters();
  const isOrgScheduleActive = hasOrgScheduleAccess && isOrgViewActive;
  const hasNoProviders =
    isOrgScheduleActive && Array.isArray(selectedProviderIds) && selectedProviderIds.length === 0;

  const { data, isLoading, isFetching, refetch } = useGetScheduleEventsQuery(
    convertToGeneratedApiParams(queryParams),
    { skip: hasNoProviders, refetchOnFocus: true, refetchOnMountOrArgChange: true },
  );

  const blockedTimes = useMemo<BlockedTimeOccurrence[]>(() => {
    if (hasNoProviders) return [];

    if (data?.blocked_times) {
      return data.blocked_times.flatMap((occurrence: ScheduleBlockedTimeOccurrenceResponse) =>
        blockedTimeOccurrenceFromScheduleEvent({
          occurrence,
          visibleResourceIds,
        }),
      );
    }

    return [];
  }, [data?.blocked_times, hasNoProviders, visibleResourceIds]);

  return {
    blockedTimes,
    isLoading,
    isFetching,
    refreshBlockedTimes: refetch,
  };
}
